import React, { useState, useEffect } from 'react';

// Focused region list for the application - US regions only
const regionList = [
  {
    name: "us-east-1",
    fullName: "US East (N. Virginia)",
    coordinates: { lat: 38.9072, lng: -77.0369 }, // Washington DC area
    timeZone: "America/New_York"
  },
  {
    name: "us-east-2", 
    fullName: "US East (Ohio)",
    coordinates: { lat: 39.9612, lng: -82.9988 }, // Columbus, Ohio
    timeZone: "America/New_York"
  },
  {
    name: "us-west-2",
    fullName: "US West (Oregon)", 
    coordinates: { lat: 45.5152, lng: -122.6784 }, // Portland, Oregon
    timeZone: "America/Los_Angeles"
  }
];

// Function to calculate distance between two coordinates (Haversine formula)
const calculateDistance = (lat1, lng1, lat2, lng2) => {
  const R = 6371; // Earth's radius in kilometers
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLng = (lng2 - lng1) * Math.PI / 180;
  const a = Math.sin(dLat/2) * Math.sin(dLat/2) +
            Math.cos(lat1 * Math.PI / 180) * Math.cos(lat2 * Math.PI / 180) *
            Math.sin(dLng/2) * Math.sin(dLng/2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
  return R * c;
};

// Function to determine the closest region based on user's location
const getClosestRegion = (userLat, userLng) => {
  let closestRegion = regionList[0];
  let minDistance = Infinity;

  regionList.forEach(region => {
    const distance = calculateDistance(
      userLat, userLng,
      region.coordinates.lat, region.coordinates.lng
    );
    if (distance < minDistance) {
      minDistance = distance;
      closestRegion = region;
    }
  });

  return closestRegion.name;
};

// Function to get user's location and suggest optimal region
const getOptimalRegion = async () => {
  try {
    // Try to get location via browser geolocation first
    if (navigator.geolocation) {
      return new Promise((resolve) => {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const { latitude, longitude } = position.coords;
            const suggestedRegion = getClosestRegion(latitude, longitude);
            resolve({ 
              region: suggestedRegion, 
              method: 'geolocation',
              location: { lat: latitude, lng: longitude }
            });
          },
          async () => {
            // Geolocation denied — fall back to default region
            resolve({ region: 'us-east-1', method: 'default' });
          },
          { timeout: 5000 }
        );
      });
    } else {
      // Browser doesn't support geolocation — use default region
      return { region: 'us-east-1', method: 'default' };
    }
  } catch (error) {
    console.log('Region detection failed, using default region');
    return { region: 'us-east-1', method: 'default' };
  }
};

const RegionList = ({ onRegionSuggestion, showLocationInfo = false }) => {
  const [locationInfo, setLocationInfo] = useState(null);
  const [isDetecting, setIsDetecting] = useState(false);

  useEffect(() => {
    const detectRegion = async () => {
      setIsDetecting(true);
      try {
        const result = await getOptimalRegion();
        setLocationInfo(result);
        if (onRegionSuggestion) {
          onRegionSuggestion(result.region, result);
        }
      } catch (error) {
        console.error('Error detecting optimal region:', error);
      } finally {
        setIsDetecting(false);
      }
    };

    detectRegion();
  }, [onRegionSuggestion]);

  const regions = regionList.map((region) => {
    const isRecommended = locationInfo && region.name === locationInfo.region;
    return (
      <option value={region.name} key={region.name}>
        {region.fullName} ({region.name}){isRecommended ? ' 🌟 Recommended' : ''}
      </option>
    );
  });

  return (
    <>
      <option value="">
        {isDetecting ? 'Detecting optimal region...' : 'Choose an AWS Region'}
      </option>
      {regions}
      {showLocationInfo && locationInfo && locationInfo.method !== 'default' && (
        <option disabled style={{ fontSize: '0.8em', fontStyle: 'italic' }}>
          ℹ️ Based on your location: {locationInfo.city ? `${locationInfo.city}, ${locationInfo.country}` : 'Detected location'}
        </option>
      )}
    </>
  );
};

// Export the regionList for use in other components
export { regionList, getOptimalRegion };
export default RegionList;
